/**
 * Pays proposé d'office dans les champs « numéro WhatsApp » des apps et des consoles
 * (`platform_config.pays_defaut`, code ISO — migration 0051). Marché de départ : le Mali.
 *
 * La liste des pays pris en charge vit dans le module partagé `otp-whatsapp` (et son jumeau Kotlin) : la base ne
 * stocke que le code ISO. Les fonctions pures (`optionsPays`, `validerPaysDefaut`) se testent sans réseau.
 */
import { PAYS, PAYS_PAR_DEFAUT, trouverPays } from '../../../../../web-commun/connexion-whatsapp/api'
import { supabase } from './supabase'

export interface OptionPays {
  valeur: string
  libelle: string
}

/** Options du sélecteur : « Mali (+223) », dans l'ordre alphabétique français. */
export function optionsPays(): OptionPays[] {
  return PAYS.map((p) => ({ valeur: p.iso, libelle: `${p.nom} (+${p.indicatif})` })).sort((a, b) =>
    a.libelle.localeCompare(b.libelle, 'fr'),
  )
}

/** Code ISO majuscule d'un pays de la table, sinon une erreur française (jamais d'écriture d'un code inconnu). */
export function validerPaysDefaut(iso: string): string {
  const p = trouverPays(iso)
  if (!p) throw new Error("Ce pays n'est pas pris en charge pour les numéros WhatsApp.")
  return p.iso
}

/** Colonne absente (migration 0051 pas encore appliquée) : une base pas à jour, pas une panne. */
export function colonneAbsente(err: { code?: string; message?: string } | null | undefined): boolean {
  if (!err) return false
  const m = err.message ?? ''
  return (
    err.code === '42703' ||
    err.code === 'PGRST204' ||
    (/pays_defaut/.test(m) && /does not exist|could not find|n'existe pas/i.test(m))
  )
}

/** Pays par défaut enregistré (le pays de départ de la plateforme si la valeur est inconnue), ou `null` si la migration 0051 n'est pas encore appliquée. */
export async function lirePaysDefautConfig(): Promise<string | null> {
  const { data, error } = await supabase.from('platform_config').select('pays_defaut').eq('id', 1).maybeSingle<{ pays_defaut: string | null }>()
  if (error) {
    if (colonneAbsente(error)) return null
    throw error
  }
  return trouverPays(data?.pays_defaut)?.iso ?? PAYS_PAR_DEFAUT
}

export async function enregistrerPaysDefaut(iso: string): Promise<void> {
  const code = validerPaysDefaut(iso)
  const { error } = await supabase.from('platform_config').update({ pays_defaut: code }).eq('id', 1)
  if (error) throw colonneAbsente(error) ? new Error('Réglage disponible après la mise à jour de la base (migration 0051).') : error
}
